<template>
  <div class="payment-wrapper">
    <div class="noise-bg"></div>
    <div class="bg-orb orb1"></div>
    <div class="bg-orb orb2"></div>
    <div class="bg-orb orb3"></div>

    <!-- Header -->
    <header class="payment-header">
      <a href="/" class="logo">
        <div class="logo-icon">nd</div>
        <span class="logo-text">new<span>dental</span></span>
      </a>
      <div class="secure-badge">
        <span class="secure-icon">🔒</span>
        <span>Pagamento seguro via Asaas</span>
      </div>
    </header>

    <main class="payment-main">

      <!-- ── TELA DE SELEÇÃO DE PLANO ── -->
      <transition name="fade" mode="out-in">
      <div v-if="screen === 'plan'" key="plan" class="screen-plan">
        <div class="plan-hero">
          <div class="section-badge">Assinar New Dental</div>
          <h1 class="plan-hero-title">Escolha como<br/><span class="gradient-text">quer pagar</span></h1>
          <p class="plan-hero-sub">14 dias grátis. Sem cartão de crédito para começar. Cancele quando quiser.</p>
        </div>

        <!-- Toggle Mensal/Anual -->
        <div class="billing-toggle">
          <button
            class="toggle-option"
            :class="{ active: billingCycle === 'monthly' }"
            @click="billingCycle = 'monthly'"
          >Mensal</button>
          <button
            class="toggle-option"
            :class="{ active: billingCycle === 'annual' }"
            @click="billingCycle = 'annual'"
          >
            Anual
            <span class="toggle-save">-30%</span>
          </button>
        </div>

        <!-- Cards de Plano -->
        <div class="plans-grid">
          <div
            v-for="plan in plans"
            :key="plan.id"
            class="plan-card"
            :class="{ featured: plan.featured, selected: selectedPlan?.id === plan.id }"
            @click="selectPlan(plan)"
          >
            <div v-if="plan.featured" class="plan-badge">⭐ Mais Popular</div>
            <div class="plan-card-inner">
              <div class="plan-name">{{ plan.name }}</div>
              <div class="plan-price-block">
                <div class="plan-price">
                  <sup>R$</sup>
                  <span>{{ billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly }}</span>
                </div>
                <div class="plan-period">/mês{{ billingCycle === 'annual' ? ' · cobrado anualmente' : '' }}</div>
              </div>
              <ul class="plan-feats">
                <li v-for="f in plan.features" :key="f">
                  <span class="feat-check">✓</span> {{ f }}
                </li>
              </ul>
              <div class="plan-select-indicator" :class="{ active: selectedPlan?.id === plan.id }">
                <span v-if="selectedPlan?.id === plan.id">✓ Selecionado</span>
                <span v-else>Selecionar</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Método de Pagamento -->
        <div v-if="selectedPlan" class="method-section">
          <div class="method-title">Forma de pagamento</div>
          <div class="methods-grid">
            <button
              v-for="m in paymentMethods"
              :key="m.id"
              class="method-btn"
              :class="{ active: selectedMethod === m.id }"
              @click="selectedMethod = m.id"
            >
              <span class="method-icon">{{ m.icon }}</span>
              <span class="method-label">{{ m.label }}</span>
            </button>
          </div>
        </div>

        <div v-if="selectedPlan && selectedMethod" class="plan-proceed">
          <div class="proceed-summary">
            <div class="proceed-plan-name">{{ selectedPlan.name }}</div>
            <div class="proceed-value">
              R$ {{ billingCycle === 'annual' ? selectedPlan.priceAnnual : selectedPlan.priceMonthly }}<span>/mês</span>
            </div>
          </div>
          <button class="btn-proceed" @click="screen = 'checkout'">
            Ir para o pagamento ›
          </button>
        </div>
      </div>

      <!-- ── TELA DE CHECKOUT ── -->
      <div v-else-if="screen === 'checkout'" key="checkout" class="screen-checkout">
        <button class="btn-back-top" @click="screen = 'plan'">‹ Voltar</button>

        <div class="checkout-layout">

          <!-- Coluna esquerda: formulário -->
          <div class="checkout-form-col">
            <div class="checkout-section-title">
              <span class="checkout-step-num">1</span> Dados pessoais
            </div>

            <div class="form-grid">
              <div class="field-group col-2">
                <label class="field-label">CPF / CNPJ *</label>
                <input v-model="checkout.cpfCnpj" type="text" class="field-input" :class="{error: errors.cpfCnpj}" placeholder="000.000.000-00" @input="maskDoc" maxlength="18"/>
                <div v-if="errors.cpfCnpj" class="field-error">{{ errors.cpfCnpj }}</div>
              </div>
              <div class="field-group col-2">
                <label class="field-label">E-mail</label>
                <input v-model="checkout.email" type="email" class="field-input" placeholder="seu@email.com"/>
              </div>
              <div class="field-group col-2">
                <label class="field-label">Telefone</label>
                <input v-model="checkout.phone" type="text" class="field-input" placeholder="(00) 00000-0000" @input="maskPhone" maxlength="15"/>
              </div>
            </div>

            <!-- PIX -->
            <template v-if="selectedMethod === 'PIX'">
              <div class="pix-info-card">
                <div class="pix-icon">⚡</div>
                <div>
                  <div class="pix-title">Pagamento via Pix</div>
                  <div class="pix-desc">Aprovação imediata 24h. Após confirmar, o QR Code será gerado.</div>
                </div>
              </div>
            </template>

            <!-- Boleto -->
            <template v-if="selectedMethod === 'BOLETO'">
              <div class="boleto-info-card">
                <div class="boleto-icon">📄</div>
                <div>
                  <div class="boleto-title">Boleto Bancário</div>
                  <div class="boleto-desc">Vencimento em 3 dias úteis. Compensação em até 2 dias após o pagamento.</div>
                </div>
              </div>
            </template>

            <div v-if="apiError" class="api-error-box">
              <span>⚠️</span> {{ apiError }}
            </div>
          </div>

          <!-- Coluna direita: resumo -->
          <div class="checkout-summary-col">
            <div class="summary-card">
              <div class="summary-header">Resumo do pedido</div>

              <div class="summary-plan-block">
                <div class="summary-plan-icon">🦷</div>
                <div>
                  <div class="summary-plan-name">New Dental {{ selectedPlan?.name }}</div>
                  <div class="summary-plan-cycle">Plano {{ billingCycle === 'annual' ? 'Anual' : 'Mensal' }}</div>
                </div>
              </div>

              <div class="summary-divider"></div>

              <div class="summary-line">
                <span>Subtotal</span>
                <span>R$ {{ currentPrice }}</span>
              </div>
              <div class="summary-line" v-if="billingCycle === 'annual'">
                <span class="saving-label">Desconto anual (30%)</span>
                <span class="saving-val">- R$ {{ annualDiscount }}</span>
              </div>
              <div class="summary-line total-line">
                <span>Total / mês</span>
                <span>R$ {{ currentPrice }}</span>
              </div>

              <div class="summary-method-row">
                <span class="smethod-icon">{{ paymentMethods.find(m => m.id === selectedMethod)?.icon }}</span>
                <span class="smethod-label">{{ paymentMethods.find(m => m.id === selectedMethod)?.label }}</span>
                <button class="smethod-change" @click="screen = 'plan'">Alterar</button>
              </div>

              <div class="summary-divider"></div>

              <button
                class="btn-pay"
                @click="handlePayment"
                :disabled="paying"
              >
                <span v-if="paying" class="spinner-sm"></span>
                <span v-else-if="selectedMethod === 'PIX'">⚡ Gerar QR Code Pix</span>
                <span v-else-if="selectedMethod === 'BOLETO'">📄 Gerar Boleto</span>
                <span v-else>🔒 Pagar R$ {{ currentPrice }}</span>
              </button>

              <div class="summary-guarantees">
                <div class="guarantee-item"><span>🔒</span> Dados criptografados</div>
                <div class="guarantee-item"><span>↩️</span> 14 dias de garantia</div>
                <div class="guarantee-item"><span>🏦</span> Processado pelo Asaas</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TELA PIX ── -->
      <div v-else-if="screen === 'pix'" key="pix" class="screen-result">
        <div class="result-card">
          <div class="result-icon pix-color">⚡</div>
          <h2 class="result-title">QR Code <span class="gradient-text">Pix</span> gerado!</h2>
          <p class="result-sub">Escaneie o código abaixo com o seu banco.<br/>Aprovação em segundos.</p>

          <div class="pix-qr-wrapper">
            <div v-if="pixData.encodedImage" class="pix-qr-image">
              <img :src="`data:image/png;base64,${pixData.encodedImage}`" alt="QR Code Pix" />
            </div>
            <div v-else class="pix-qr-placeholder">
              <div class="qr-fake"></div>
            </div>
          </div>

          <div class="pix-copy-section">
            <div class="pix-copy-label">Pix Copia e Cola</div>
            <div class="pix-copy-row">
              <input
                :value="pixData.payload || 'Gerando código...'"
                readonly
                class="pix-copy-input field-input"
                ref="pixInput"
              />
              <button class="pix-copy-btn" @click="copyPix">
                {{ pixCopied ? '✓' : '📋' }}
              </button>
            </div>
          </div>

          <div class="pix-expire">
            <span class="expire-icon">⏱</span>
            Válido por
            <span class="expire-count">{{ pixCountdown }}</span>
            minutos
          </div>

          <div class="pix-status-row">
            <div class="status-dot" :class="pixPaid ? 'paid' : 'pending'"></div>
            <span>{{ pixPaid ? '✅ Pagamento confirmado!' : 'Aguardando pagamento...' }}</span>
          </div>

          <div v-if="pixPaid" class="result-success-actions">
            <a href="/app" class="btn-proceed">Acessar a plataforma ›</a>
          </div>
          <div v-else class="result-success-actions">
            <a href="/app" class="btn-proceed-ghost">Já paguei, acessar plataforma ›</a>
          </div>
        </div>
      </div>

      <!-- ── TELA BOLETO ── -->
      <div v-else-if="screen === 'boleto'" key="boleto" class="screen-result">
        <div class="result-card">
          <div class="result-icon">📄</div>
          <h2 class="result-title">Boleto <span class="gradient-text">gerado!</span></h2>
          <p class="result-sub">Pague antes do vencimento para ativar o seu plano.</p>

          <div class="boleto-line-wrapper">
            <div class="boleto-line-label">Linha digitável</div>
            <div class="boleto-line-box">
              <span class="boleto-line-text">{{ boletoData.identificationField || '00000.00000 00000.000000 00000.000000 0 00000000000000' }}</span>
            </div>
            <button class="btn-copy-line" @click="copyBoleto">
              {{ boletoCopied ? '✓ Copiado!' : '📋 Copiar linha digitável' }}
            </button>
          </div>

          <div class="boleto-actions">
            <a
              v-if="boletoData.bankSlipUrl"
              :href="boletoData.bankSlipUrl"
              target="_blank"
              class="btn-boleto-pdf"
            >
              📥 Abrir PDF do Boleto
            </a>
          </div>

          <div class="boleto-info-row">
            <div class="binfo-item">
              <div class="binfo-label">Vencimento</div>
              <div class="binfo-val">{{ boletoData.dueDate || formatDueDate() }}</div>
            </div>
            <div class="binfo-item">
              <div class="binfo-label">Valor</div>
              <div class="binfo-val">R$ {{ currentPrice }}</div>
            </div>
            <div class="binfo-item">
              <div class="binfo-label">Status</div>
              <div class="binfo-val status-pending">Aguardando</div>
            </div>
          </div>

          <div class="result-success-actions" style="margin-top: 24px;">
            <a href="/app" class="btn-proceed-ghost">Já paguei, acessar plataforma ›</a>
          </div>
        </div>
      </div>

      <!-- ── TELA SUCESSO CARTÃO ── -->
      <div v-else-if="screen === 'success'" key="success" class="screen-result">
        <div class="result-card success-glow">
          <div class="success-animation">
            <div class="success-circle">
              <span class="success-check">✓</span>
            </div>
            <div class="ring ring1"></div>
            <div class="ring ring2"></div>
            <div class="ring ring3"></div>
          </div>
          <h2 class="result-title">Pagamento <span class="gradient-text">confirmado!</span></h2>
          <p class="result-sub">Seu plano <strong>{{ selectedPlan?.name }}</strong> está ativo.<br/>Pode começar a usar o New Dental agora mesmo.</p>
          <div class="success-meta">
            <div class="smeta-item">
              <span class="smeta-label">Plano</span>
              <span class="smeta-val">New Dental {{ selectedPlan?.name }}</span>
            </div>
            <div class="smeta-item">
              <span class="smeta-label">Valor</span>
              <span class="smeta-val">R$ {{ currentPrice }}/mês</span>
            </div>
          </div>
          <a href="/app" class="btn-proceed">Aceder à plataforma ›</a>
        </div>
      </div>

      </transition>
    </main>
  </div>
</template>

<script>
function getApiUrl() {
  const runtimeVariables = window.variables || {};
  return runtimeVariables.API || process.env.VUE_APP_API_URL || 'http://localhost:3001';
}

export default {
  name: 'PaymentPlan',

  data() {
    return {
      screen: 'plan',
      billingCycle: 'monthly',
      selectedPlan: null,
      selectedMethod: 'PIX',
      paying: false,
      apiError: '',
      companyId: null,
      pixData: {},
      boletoData: {},
      pixCopied: false,
      boletoCopied: false,
      pixCountdown: 30,
      pixInterval: null,
      pixPaid: false,
      pixPollInterval: null,
      checkout: { cpfCnpj: '', email: '', phone: '' },
      errors: {},
      plans: [],
      paymentMethods: [
        { id: 'PIX',    icon: '⚡', label: 'Pix' },
        { id: 'BOLETO', icon: '📄', label: 'Boleto' },
      ],
    };
  },

  computed: {
    currentPrice() {
      if (!this.selectedPlan) return '0';
      return this.billingCycle === 'annual' ? this.selectedPlan.priceAnnual : this.selectedPlan.priceMonthly;
    },
    annualDiscount() {
      if (!this.selectedPlan) return '0';
      return (this.selectedPlan.rawMonthly - this.selectedPlan.rawAnnual).toFixed(0);
    },
  },

  async created() {
    try {
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/account/plans`);
      const json = await res.json();
      const raw = json.data ?? json;
      this.plans = raw.map((p, index) => {
        const users = p.maxUsers;
        const patients = p.maxPatients;
        const features = [];
        features.push(users === null ? 'Dentistas ilimitados' : users === 1 ? '1 dentista' : `Até ${users} dentistas`);
        features.push(patients === null ? 'Pacientes ilimitados' : `Até ${patients} pacientes`);
        features.push('Agenda completa', 'Odontograma', 'Anamnese');
        if (users === null || (users !== null && users > 1)) features.push('DSD incluído');
        if (users === null) features.push('Todos os módulos', 'Suporte prioritário');
        return {
          id: p.id,
          name: p.name,
          priceMonthly: Math.floor(p.value).toString(),
          priceAnnual: Math.floor(p.value * 0.7).toString(),
          rawMonthly: p.value,
          rawAnnual: parseFloat((p.value * 0.7).toFixed(2)),
          featured: index === 1,
          features,
        };
      });

      const { planId, billing, companyId } = this.$route.query;
      if (billing === 'annual') this.billingCycle = 'annual';
      if (companyId) this.companyId = companyId;
      if (planId) {
        const found = this.plans.find(p => String(p.id) === String(planId));
        if (found) this.selectPlan(found);
      }
    } catch (_) {}
  },

  methods: {
    selectPlan(plan) {
      this.selectedPlan = plan;
    },

    validate() {
      const e = {};
      if (!this.checkout.cpfCnpj.trim()) e.cpfCnpj = 'CPF/CNPJ é obrigatório.';
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    maskDoc(ev) {
      let v = ev.target.value.replace(/\D/g, '');
      if (v.length <= 11) {
        v = v.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        v = v.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
      }
      this.checkout.cpfCnpj = v;
    },

    maskPhone(ev) {
      let v = ev.target.value.replace(/\D/g, '');
      v = v.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
      this.checkout.phone = v;
    },

    formatDueDate() {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      return d.toLocaleDateString('pt-BR');
    },

    async handlePayment() {
      if (!this.validate()) return;
      this.paying = true;
      this.apiError = '';
      try {
        const apiUrl = getApiUrl();
        const res = await fetch(`${apiUrl}/account/subscribe-public`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: this.companyId,
            planId: this.selectedPlan.id,
            billingType: this.selectedMethod,
            cpfCnpj: this.checkout.cpfCnpj.replace(/\D/g, '') || undefined,
            email: this.checkout.email || undefined,
            phone: this.checkout.phone.replace(/\D/g, '') || undefined,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.data?.message || json?.message || 'Erro ao processar pagamento.');
        const data = json.data;
        if (this.selectedMethod === 'PIX') {
          this.pixData = data.pixQrCode || {};
          this.screen = 'pix';
          this.startPixCountdown();
          this.startPixPolling();
        } else {
          this.boletoData = data.bankSlip || {};
          this.screen = 'boleto';
        }
      } catch (err) {
        this.apiError = err.message || 'Erro ao processar pagamento. Tente novamente.';
      } finally {
        this.paying = false;
      }
    },

    startPixCountdown() {
      this.pixCountdown = 30;
      this.pixInterval = setInterval(() => {
        if (this.pixCountdown > 0) this.pixCountdown--;
        else clearInterval(this.pixInterval);
      }, 60000);
    },

    startPixPolling() {
      if (!this.companyId) return;
      this.pixPollInterval = setInterval(async () => {
        try {
          const apiUrl = getApiUrl();
          const res = await fetch(`${apiUrl}/account/subscription-status-public?companyId=${this.companyId}`);
          const json = await res.json();
          const status = json?.data?.status;
          if (status === 'active' || status === 'Active' || status === 1) {
            this.pixPaid = true;
            clearInterval(this.pixPollInterval);
            clearInterval(this.pixInterval);
          }
        } catch (_) {}
      }, 5000);
    },

    copyPix() {
      navigator.clipboard.writeText(this.pixData.payload || '');
      this.pixCopied = true;
      setTimeout(() => { this.pixCopied = false; }, 2500);
    },

    copyBoleto() {
      navigator.clipboard.writeText(this.boletoData.identificationField || '');
      this.boletoCopied = true;
      setTimeout(() => { this.boletoCopied = false; }, 2500);
    },
  },

  beforeDestroy() {
    clearInterval(this.pixInterval);
    clearInterval(this.pixPollInterval);
  },
};
</script>

<style lang="scss" scoped>
$primary:    #8E44AD;
$tertiary:   #9D4EBE;
$quaternary: #862AAC;
$dark:       #0D0618;
$dark2:      #160D22;
$glass:      rgba(255,255,255,0.06);
$glass-border: rgba(255,255,255,0.12);
$green:      #00c878;

@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Syne:wght@700;800&display=swap');

/* ── BASE ── */
.payment-wrapper {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: $dark; color: #fff;
  min-height: 100vh; position: relative; overflow-x: hidden;
}

.noise-bg { position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: .4; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"); }
.bg-orb { position: fixed; border-radius: 50%; filter: blur(90px); pointer-events: none; z-index: 0; }
.orb1 { width: 700px; height: 700px; background: radial-gradient(circle, rgba(142,68,173,.28) 0%, transparent 70%); top: -200px; left: -250px; }
.orb2 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(134,42,172,.18) 0%, transparent 70%); bottom: -100px; right: -200px; }
.orb3 { width: 350px; height: 350px; background: radial-gradient(circle, rgba(0,200,120,.1) 0%, transparent 70%); top: 50%; left: 60%; }

/* ── HEADER ── */
.payment-header {
  position: sticky; top: 0; z-index: 100;
  padding: 14px 40px; background: rgba(13,6,24,.85); backdrop-filter: blur(20px);
  border-bottom: 1px solid $glass-border;
  display: flex; align-items: center; justify-content: space-between;
}
.logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.logo-icon { width: 34px; height: 34px; background: linear-gradient(135deg, $primary, $tertiary); border-radius: 9px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 12px; color: #fff; box-shadow: 0 4px 14px rgba(142,68,173,.5); }
.logo-text { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; color: #fff; span { color: $tertiary; } }
.secure-badge { display: flex; align-items: center; gap: 7px; font-size: 12px; color: rgba(255,255,255,.5); background: rgba(0,200,120,.08); border: 1px solid rgba(0,200,120,.2); padding: 5px 12px; border-radius: 100px; .secure-icon { font-size: 13px; } }

/* ── MAIN ── */
.payment-main { position: relative; z-index: 1; max-width: 1100px; margin: 0 auto; padding: 48px 24px 80px; }

/* ── PLAN SCREEN ── */
.screen-plan {}
.plan-hero { text-align: center; margin-bottom: 40px; }
.section-badge { display: inline-block; background: rgba(142,68,173,.15); border: 1px solid rgba(142,68,173,.3); color: #c77dff; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 4px 12px; border-radius: 100px; margin-bottom: 12px; }
.plan-hero-title { font-family: 'Syne', sans-serif; font-size: clamp(32px,5vw,56px); font-weight: 800; letter-spacing: -1.5px; margin-bottom: 10px; }
.plan-hero-sub { font-size: 15px; color: rgba(255,255,255,.55); }
.gradient-text { background: linear-gradient(135deg, #c77dff, $quaternary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

/* Billing toggle */
.billing-toggle { display: flex; background: rgba(255,255,255,.06); border: 1px solid $glass-border; border-radius: 100px; padding: 4px; width: fit-content; margin: 0 auto 40px; gap: 4px; }
.toggle-option { background: transparent; border: none; color: rgba(255,255,255,.5); font-family: inherit; font-size: 13px; font-weight: 600; padding: 8px 22px; border-radius: 100px; cursor: pointer; transition: all .25s; display: flex; align-items: center; gap: 8px; &.active { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; box-shadow: 0 4px 16px rgba(142,68,173,.4); } }
.toggle-save { background: rgba(0,200,120,.2); border: 1px solid rgba(0,200,120,.3); color: #00c878; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 100px; }

/* Plans grid */
.plans-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 16px; margin-bottom: 40px; }
.plan-card {
  background: $glass; border: 1.5px solid $glass-border; border-radius: 22px; cursor: pointer;
  transition: all .3s ease; position: relative;
  &:hover { border-color: rgba(142,68,173,.45); transform: translateY(-3px); }
  &.featured { background: rgba(142,68,173,.12); border-color: rgba(142,68,173,.4); box-shadow: 0 0 0 1px rgba(142,68,173,.1); }
  &.selected { border-color: $primary; box-shadow: 0 0 0 3px rgba(142,68,173,.2); transform: translateY(-3px); }
}
.plan-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, $primary, $quaternary); color: #fff; font-size: 11px; font-weight: 700; padding: 4px 16px; border-radius: 100px; white-space: nowrap; }
.plan-card-inner { padding: 28px 24px; }
.plan-name { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,.45); margin-bottom: 12px; }
.plan-price-block { margin-bottom: 16px; }
.plan-price { font-family: 'Syne', sans-serif; font-size: 42px; font-weight: 800; color: #fff; line-height: 1; sup { font-size: 18px; vertical-align: top; margin-top: 10px; } }
.plan-period { font-size: 12px; color: rgba(255,255,255,.35); margin-top: 4px; }
.plan-feats { list-style: none; padding: 0; margin: 0 0 20px; display: flex; flex-direction: column; gap: 8px; li { font-size: 13px; color: rgba(255,255,255,.65); display: flex; align-items: center; gap: 8px; } }
.feat-check { width: 16px; height: 16px; background: rgba(142,68,173,.2); border: 1px solid rgba(142,68,173,.35); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; color: #c77dff; flex-shrink: 0; }
.plan-select-indicator { border: 1px solid rgba(255,255,255,.12); border-radius: 10px; padding: 10px; text-align: center; font-size: 13px; font-weight: 600; color: rgba(255,255,255,.4); transition: all .2s; .plan-card.selected & { background: rgba(142,68,173,.2); border-color: rgba(142,68,173,.5); color: #c77dff; } }

/* Method */
.method-section { margin-bottom: 32px; }
.method-title { font-size: 13px; font-weight: 700; color: rgba(255,255,255,.5); text-transform: uppercase; letter-spacing: .8px; margin-bottom: 14px; }
.methods-grid { display: flex; gap: 10px; flex-wrap: wrap; }
.method-btn { background: $glass; border: 1.5px solid $glass-border; border-radius: 14px; padding: 12px 20px; cursor: pointer; display: flex; align-items: center; gap: 10px; color: rgba(255,255,255,.6); font-family: inherit; font-size: 14px; font-weight: 600; transition: all .2s; &.active { background: rgba(142,68,173,.18); border-color: rgba(142,68,173,.5); color: #c77dff; } &:hover:not(.active) { border-color: rgba(255,255,255,.2); } }
.method-icon { font-size: 18px; }

/* Proceed */
.plan-proceed { background: $glass; border: 1px solid $glass-border; border-radius: 20px; padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
.proceed-summary { display: flex; flex-direction: column; gap: 3px; }
.proceed-plan-name { font-size: 14px; font-weight: 700; color: #fff; }
.proceed-value { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 800; color: #c77dff; span { font-size: 13px; color: rgba(255,255,255,.4); font-family: inherit; } }

/* ── CHECKOUT SCREEN ── */
.screen-checkout {}
.btn-back-top { background: none; border: none; color: rgba(255,255,255,.5); font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; padding: 0 0 24px; display: flex; align-items: center; gap: 6px; transition: color .2s; &:hover { color: #fff; } }
.checkout-layout { display: grid; grid-template-columns: 1fr 360px; gap: 32px; align-items: start; @media (max-width: 900px) { grid-template-columns: 1fr; } }
.checkout-section-title { display: flex; align-items: center; gap: 12px; font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; color: #fff; margin-bottom: 20px; }
.checkout-step-num { width: 28px; height: 28px; background: linear-gradient(135deg, $primary, $quaternary); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }

/* Form */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; @media (max-width: 520px) { grid-template-columns: 1fr; } }
.col-2 { grid-column: span 2; @media (max-width: 520px) { grid-column: span 1; } }
.field-group { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 11px; font-weight: 600; color: rgba(255,255,255,.5); text-transform: uppercase; letter-spacing: .6px; }
.field-input { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 12px; padding: 12px 16px; font-size: 14px; color: #fff; font-family: inherit; width: 100%; transition: all .2s; outline: none; box-sizing: border-box; &::placeholder { color: rgba(255,255,255,.22); } &:focus { border-color: rgba(142,68,173,.6); background: rgba(142,68,173,.08); box-shadow: 0 0 0 3px rgba(142,68,173,.12); } &.error { border-color: rgba(255,80,80,.5); background: rgba(255,80,80,.05); } }
.field-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.3)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 38px; cursor: pointer; option { background: $dark2; color: #fff; } }
.field-error { font-size: 11px; color: #ff7070; font-weight: 600; }

/* ── CARD FLIP VISUAL ── */
.card-visual-wrapper { perspective: 1200px; margin: 0 auto 24px; width: 300px; height: 170px; }
.card-visual { width: 100%; height: 100%; position: relative; transform-style: preserve-3d; transition: transform .6s cubic-bezier(.4,0,.2,1); &.flipped { transform: rotateY(180deg); } }
.card-front, .card-back {
  position: absolute; inset: 0; backface-visibility: hidden;
  background: linear-gradient(135deg, #1a0b2e 0%, #2d1150 50%, #1a0b2e 100%);
  border: 1px solid rgba(142,68,173,.4); border-radius: 18px;
  padding: 20px; box-shadow: 0 20px 60px rgba(0,0,0,.5);
}
.card-back { transform: rotateY(180deg); }
.card-chip { width: 34px; height: 26px; background: linear-gradient(135deg, #e8c96c, #c9a227); border-radius: 6px; margin-bottom: 20px; }
.card-number-display { font-family: 'Courier New', monospace; font-size: 16px; letter-spacing: 2px; color: rgba(255,255,255,.9); margin-bottom: 16px; }
.card-bottom-row { display: flex; justify-content: space-between; }
.card-field-label { font-size: 9px; color: rgba(255,255,255,.4); text-transform: uppercase; letter-spacing: .8px; margin-bottom: 3px; }
.card-field-value { font-size: 13px; font-weight: 600; color: #fff; font-family: 'Courier New', monospace; letter-spacing: 1px; }
.card-back-stripe { height: 36px; background: rgba(0,0,0,.5); margin: 18px -20px 16px; }
.card-back-ccv-row { display: flex; justify-content: flex-end; align-items: center; gap: 12px; }
.card-back-ccv-label { font-size: 11px; color: rgba(255,255,255,.5); }
.card-back-ccv-box { background: rgba(255,255,255,.9); color: #222; font-family: 'Courier New', monospace; font-size: 14px; font-weight: 700; padding: 6px 16px; border-radius: 6px; min-width: 50px; text-align: center; }

/* Info cards */
.pix-info-card, .boleto-info-card { display: flex; align-items: center; gap: 16px; background: rgba(0,200,120,.08); border: 1px solid rgba(0,200,120,.2); border-radius: 14px; padding: 18px; margin-top: 20px; }
.pix-icon, .boleto-icon { font-size: 28px; }
.pix-title, .boleto-title { font-size: 15px; font-weight: 700; color: #fff; margin-bottom: 4px; }
.pix-desc, .boleto-desc { font-size: 13px; color: rgba(255,255,255,.55); }

.api-error-box { background: rgba(255,80,80,.08); border: 1px solid rgba(255,80,80,.25); border-radius: 12px; padding: 14px 16px; color: #ff9090; font-size: 13px; margin-top: 16px; display: flex; gap: 10px; align-items: center; }

/* ── CHECKOUT SUMMARY ── */
.checkout-summary-col { position: sticky; top: 80px; }
.summary-card { background: rgba(22,13,34,.9); border: 1px solid $glass-border; border-radius: 22px; padding: 28px; backdrop-filter: blur(16px); }
.summary-header { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; color: #fff; margin-bottom: 20px; }
.summary-plan-block { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.summary-plan-icon { width: 40px; height: 40px; background: rgba(142,68,173,.2); border: 1px solid rgba(142,68,173,.3); border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
.summary-plan-name { font-size: 14px; font-weight: 700; color: #fff; }
.summary-plan-cycle { font-size: 12px; color: rgba(255,255,255,.4); }
.summary-divider { height: 1px; background: $glass-border; margin: 16px 0; }
.summary-line { display: flex; justify-content: space-between; font-size: 13px; color: rgba(255,255,255,.6); margin-bottom: 10px; &.total-line { font-size: 15px; font-weight: 700; color: #fff; } }
.saving-label { color: $green; font-weight: 600; }
.saving-val { color: $green; font-weight: 600; }
.summary-method-row { display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,.04); border: 1px solid $glass-border; border-radius: 10px; padding: 10px 14px; }
.smethod-icon { font-size: 18px; }
.smethod-label { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.75); flex: 1; }
.smethod-change { background: none; border: none; cursor: pointer; font-family: inherit; font-size: 11px; font-weight: 700; color: #c77dff; text-transform: uppercase; letter-spacing: .6px; &:hover { text-decoration: underline; } }

.btn-pay { width: 100%; padding: 15px; background: linear-gradient(135deg, $primary, $quaternary); border: none; border-radius: 14px; color: #fff; font-family: inherit; font-size: 15px; font-weight: 700; cursor: pointer; transition: all .3s; box-shadow: 0 8px 24px rgba(142,68,173,.4); margin-bottom: 16px; display: flex; align-items: center; justify-content: center; gap: 8px; &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(142,68,173,.6); } &:disabled { opacity: .6; cursor: not-allowed; } }

.summary-guarantees { display: flex; flex-direction: column; gap: 8px; }
.guarantee-item { display: flex; align-items: center; gap: 8px; font-size: 12px; color: rgba(255,255,255,.4); }

/* ── PROCEED BTN ── */
.btn-proceed { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; padding: 14px 32px; border-radius: 100px; font-size: 14px; font-weight: 700; border: none; cursor: pointer; font-family: inherit; transition: all .3s; box-shadow: 0 8px 24px rgba(142,68,173,.4); display: inline-flex; align-items: center; gap: 8px; text-decoration: none; &:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(142,68,173,.6); } }
.btn-proceed-ghost { background: transparent; color: rgba(255,255,255,.45); padding: 12px 24px; border-radius: 100px; font-size: 13px; font-weight: 600; border: 1px solid rgba(255,255,255,.12); cursor: pointer; font-family: inherit; transition: all .3s; display: inline-flex; align-items: center; gap: 8px; text-decoration: none; &:hover { border-color: rgba(255,255,255,.3); color: rgba(255,255,255,.75); } }

/* ── RESULT SCREENS ── */
.screen-result { display: flex; justify-content: center; padding: 20px 0; }
.result-card { background: rgba(22,13,34,.9); border: 1px solid $glass-border; border-radius: 28px; padding: 48px 40px; max-width: 540px; width: 100%; text-align: center; box-shadow: 0 40px 100px rgba(0,0,0,.4); }
.result-icon { font-size: 48px; margin-bottom: 20px; display: block; }
.pix-color { filter: drop-shadow(0 0 16px rgba(0,200,120,.5)); }
.result-title { font-family: 'Syne', sans-serif; font-size: clamp(26px,4vw,36px); font-weight: 800; letter-spacing: -1px; margin-bottom: 12px; }
.result-sub { font-size: 14px; color: rgba(255,255,255,.55); line-height: 1.7; margin-bottom: 28px; }

/* PIX Result */
.pix-qr-wrapper { display: flex; justify-content: center; margin-bottom: 24px; }
.pix-qr-image { background: #fff; border-radius: 16px; padding: 12px; width: 180px; height: 180px; display: flex; align-items: center; justify-content: center; img { width: 100%; height: 100%; object-fit: contain; } }
.qr-fake { width: 156px; height: 156px; background: repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 0 0 / 8px 8px; border-radius: 8px; }
.pix-copy-section { margin-bottom: 20px; text-align: left; }
.pix-copy-label { font-size: 11px; font-weight: 700; color: rgba(255,255,255,.4); text-transform: uppercase; letter-spacing: .8px; margin-bottom: 8px; }
.pix-copy-row { display: flex; gap: 8px; }
.pix-copy-input { flex: 1; font-size: 11px; font-family: monospace; }
.pix-copy-btn { width: 44px; flex-shrink: 0; background: rgba(142,68,173,.2); border: 1px solid rgba(142,68,173,.3); border-radius: 12px; cursor: pointer; font-size: 16px; color: #fff; display: flex; align-items: center; justify-content: center; transition: all .2s; &:hover { background: rgba(142,68,173,.35); } }
.pix-expire { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,200,0,.08); border: 1px solid rgba(255,200,0,.2); border-radius: 100px; padding: 6px 14px; font-size: 12px; color: rgba(255,200,100,.8); margin-bottom: 16px; }
.expire-icon { font-size: 13px; }
.expire-count { font-weight: 800; color: #ffd060; }
.pix-status-row { display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 13px; color: rgba(255,255,255,.6); margin-bottom: 24px; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; &.pending { background: #ffd060; box-shadow: 0 0 8px rgba(255,208,96,.5); animation: blink 1.5s ease-in-out infinite; } &.paid { background: $green; box-shadow: 0 0 8px rgba(0,200,120,.5); } }
@keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
.result-success-actions { margin-top: 8px; display: flex; justify-content: center; }

/* Boleto */
.boleto-line-wrapper { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.1); border-radius: 14px; padding: 18px; margin-bottom: 20px; text-align: left; }
.boleto-line-label { font-size: 11px; font-weight: 700; color: rgba(255,255,255,.35); text-transform: uppercase; letter-spacing: .8px; margin-bottom: 8px; }
.boleto-line-box { background: rgba(255,255,255,.06); border-radius: 8px; padding: 10px 12px; margin-bottom: 12px; }
.boleto-line-text { font-family: monospace; font-size: 12px; color: rgba(255,255,255,.7); word-break: break-all; line-height: 1.6; }
.btn-copy-line { background: rgba(142,68,173,.15); border: 1px solid rgba(142,68,173,.3); color: #c77dff; border-radius: 10px; padding: 10px 18px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .2s; width: 100%; &:hover { background: rgba(142,68,173,.25); } }
.boleto-actions { margin-bottom: 24px; display: flex; justify-content: center; }
.btn-boleto-pdf { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; padding: 12px 28px; border-radius: 12px; font-size: 14px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; transition: all .2s; &:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(142,68,173,.4); } }
.boleto-info-row { display: flex; justify-content: space-around; background: rgba(255,255,255,.04); border-radius: 14px; padding: 16px; }
.binfo-label { font-size: 11px; color: rgba(255,255,255,.35); text-transform: uppercase; letter-spacing: .6px; margin-bottom: 4px; }
.binfo-val { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: #fff; &.status-pending { color: #ffd060; } }

/* ── SUCCESS ── */
.success-glow { box-shadow: 0 0 60px rgba(0,200,120,.12), 0 40px 100px rgba(0,0,0,.4); }
.success-animation { position: relative; width: 100px; height: 100px; margin: 0 auto 28px; }
.success-circle { width: 80px; height: 80px; background: linear-gradient(135deg, $green, #00a061); border-radius: 50%; display: flex; align-items: center; justify-content: center; position: relative; z-index: 2; margin: 10px auto 0; box-shadow: 0 12px 36px rgba(0,200,120,.4); animation: pop-in .5s cubic-bezier(.175,.885,.32,1.275); }
.success-check { font-size: 30px; color: #fff; }
.ring { position: absolute; border-radius: 50%; border: 1px solid rgba(0,200,120,.25); animation: ring-expand 1.5s ease-out infinite; top: 50%; left: 50%; transform-origin: center; }
.ring1 { width: 90px; height: 90px; margin: -45px; animation-delay: 0s; }
.ring2 { width: 110px; height: 110px; margin: -55px; animation-delay: .3s; }
.ring3 { width: 130px; height: 130px; margin: -65px; animation-delay: .6s; }
@keyframes ring-expand { 0% { transform: scale(.6); opacity: .8; } 100% { transform: scale(1.3); opacity: 0; } }
@keyframes pop-in { 0% { transform: scale(0); } 60% { transform: scale(1.1); } 100% { transform: scale(1); } }
.success-meta { background: rgba(255,255,255,.04); border: 1px solid $glass-border; border-radius: 14px; padding: 18px; margin-bottom: 28px; display: flex; flex-direction: column; gap: 10px; text-align: left; }
.smeta-item { display: flex; justify-content: space-between; align-items: center; }
.smeta-label { font-size: 12px; color: rgba(255,255,255,.4); }
.smeta-val { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); &.mono { font-family: monospace; font-size: 11px; } }

/* ── SPINNERS ── */
.spinner-sm { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── TRANSITION ── */
.fade-enter-active, .fade-leave-active { transition: opacity .3s ease, transform .3s ease; }
.fade-enter-from { opacity: 0; transform: translateY(16px); }
.fade-leave-to { opacity: 0; transform: translateY(-16px); }
</style>
